import { TextAlignment, VerticalAlignment } from '../../../../../../../common/table/CellTypeConstants';
import { getAllCellsBetween } from '../../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';
import { getCellTextAlignment } from '../../dataUtils/cell/tableCellDisplayUtils';
import { TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT } from '../../../../../../../common/table/tableConstants';
import type { MNHotInstance } from '../../../../bridge/tableHotInstanceTypes';
import type { CellSelections } from '../../../../../../../common/table/TableTypes';

/**
 * Determines whether the current selection contains any table cells.
 */
export const hasHotTableCellSelection = (cellSelections?: CellSelections): boolean => !!cellSelections?.length;

/**
 * Determines the text alignment shared by all selected cells.
 * If there is no shared alignment, returns undefined.
 */
export const getHotTableCommonTextAlignment = (
    hotInstance: MNHotInstance,
    locale: string,
    cellSelections = hotInstance.getSelected(),
): TextAlignment | undefined => {
    if (!hasHotTableCellSelection(cellSelections)) return;

    const [firstCell, ...otherCells] = getAllCellsBetween(cellSelections);
    const { cellData } = hotInstance.getCellMeta(firstCell.row, firstCell.col);
    const displayValue = hotInstance.getDataAtCell(firstCell.row, firstCell.col);
    const firstTextAlignment = getCellTextAlignment(cellData, locale, displayValue);

    if (!firstTextAlignment) return;

    const hasDifferentTextAlignments = otherCells.some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);
        const currentDisplayValue = hotInstance.getDataAtCell(row, col);
        const textAlignment = getCellTextAlignment(cellData, locale, currentDisplayValue);

        return textAlignment !== firstTextAlignment;
    });

    if (hasDifferentTextAlignments) return;

    return firstTextAlignment;
};

/**
 * Determines the vertical alignment shared by all selected cells.
 * If there is no shared alignment, returns the default vertical alignment.
 */
export const getHotTableCommonVerticalAlignment = (
    hotInstance: MNHotInstance,
    cellSelections = hotInstance.getSelected(),
): VerticalAlignment | undefined => {
    if (!hasHotTableCellSelection(cellSelections)) return;

    const [firstCell, ...otherCells] = getAllCellsBetween(cellSelections);
    const { cellData } = hotInstance.getCellMeta(firstCell.row, firstCell.col);
    const firstVerticalAlignment = cellData?.verticalAlignment;

    if (!firstVerticalAlignment) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    const hasDifferentVerticalAlignments = otherCells.some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);
        return cellData?.verticalAlignment !== firstVerticalAlignment;
    });

    if (hasDifferentVerticalAlignments) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    return firstVerticalAlignment;
};

/**
 * Determines if the provided text alignment is applied to all selected cells.
 */
export const isHotTableTextAlignmentApplied = (
    hotInstance: MNHotInstance,
    textAlignment: TextAlignment,
    locale: string,
    cellSelections = hotInstance.getSelected(),
): boolean => {
    if (!hasHotTableCellSelection(cellSelections)) return false;

    return !getAllCellsBetween(cellSelections).some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);
        const hotComputedCellValue = hotInstance.getDataAtCell(row, col); // For formulas, will return formula result

        return getCellTextAlignment(cellData, locale, hotComputedCellValue) !== textAlignment;
    });
};

/**
 * Determines if the provided vertical alignment is applied to all selected cells.
 */
export const isHotTableVerticalAlignmentApplied = (
    hotInstance: MNHotInstance,
    verticalAlignment: VerticalAlignment,
    cellSelections = hotInstance?.getSelected(),
): boolean => {
    if (!hasHotTableCellSelection(cellSelections)) return false;

    return !getAllCellsBetween(cellSelections).some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);

        if (!cellData?.verticalAlignment) return verticalAlignment !== TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

        return cellData.verticalAlignment !== verticalAlignment;
    });
};
